/**
 * GroundingImagePicker component — Grid of location photos for image grounding.
 *
 * Displays grounding images from the tiered cascade (admin-uploaded or API-fetched)
 * with source badges, attribution text, and a "Create from scratch" fallback option.
 *
 * Design: DENiZEN
 * - Soft borders on cards
 * - Source badges: mid-green (admin), teal (Geograph), teal (Mapillary)
 * - Selected state: mid-green ring + shadow-denizen
 * - WCAG 2.1 AA: alt text, keyboard nav, focus-visible
 */

import { useState, useCallback, useRef } from 'react'
import type { GroundingImageOption } from '@/services/api'

/** Colour mapping for source badges */
const SOURCE_COLOURS: Record<string, { bg: string; label: string }> = {
  admin: { bg: 'bg-dark-green', label: 'Curated' },
  geograph: { bg: 'bg-dark-green', label: 'Geograph' },
  mapillary: { bg: 'bg-dark-green', label: 'Mapillary' },
}

export interface GroundingImagePickerProps {
  /** Available grounding images */
  images: GroundingImageOption[]
  /** Callback when a grounding image is selected */
  onSelect: (imageId: string) => void
  /** Callback when "Create from scratch" is chosen */
  onCreateFromScratch: () => void
  /** Whether selection is disabled (e.g. during generation) */
  isDisabled?: boolean
  /** Location name to display */
  location?: string
}

export function GroundingImagePicker({
  images,
  onSelect,
  onCreateFromScratch,
  isDisabled = false,
  location,
}: GroundingImagePickerProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const handleSelect = useCallback(
    (imageId: string) => {
      if (isDisabled) return
      setSelectedId(imageId)
      onSelect(imageId)
    },
    [isDisabled, onSelect]
  )

  /** Arrow-key navigation for radiogroup (WCAG) */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const grid = gridRef.current
      if (!grid) return

      const arrows = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp']
      if (!arrows.includes(e.key)) return

      e.preventDefault()

      const buttons = Array.from(
        grid.querySelectorAll<HTMLButtonElement>('button[role="radio"]')
      )
      if (buttons.length === 0) return

      const currentIndex = buttons.findIndex(
        (btn) => btn === document.activeElement
      )

      let nextIndex: number
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % buttons.length
      } else {
        nextIndex =
          currentIndex < 0
            ? buttons.length - 1
            : (currentIndex - 1 + buttons.length) % buttons.length
      }

      buttons[nextIndex]?.focus()
    },
    []
  )

  if (images.length === 0) return null

  return (
    <div className="w-full space-y-4">
      {/* Section heading */}
      <div>
        <h3 className="text-lg font-display font-black text-dark-green mb-1">
          Photos of {location || 'Your Area'}
        </h3>
        <p className="text-sm font-body text-muted">
          Select a photo as a starting point, or create from scratch below
        </p>
      </div>

      {/* Image grid */}
      <div
        ref={gridRef}
        className="grid grid-cols-2 md:grid-cols-3 gap-3"
        role="radiogroup"
        aria-label="Choose a grounding image"
        onKeyDown={handleKeyDown}
      >
        {images.map((image) => {
          const isSelected = selectedId === image.id
          const sourceStyle = SOURCE_COLOURS[image.source] ?? { bg: 'bg-dark-green', label: 'Curated' }

          return (
            <button
              key={image.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={
                image.title
                  ? `${image.title} from ${sourceStyle.label}`
                  : `Photo from ${sourceStyle.label}`
              }
              disabled={isDisabled}
              onClick={() => handleSelect(image.id)}
              className={`
                relative overflow-hidden bg-white rounded-2xl transition-all duration-150
                ${isSelected
                  ? 'border-2 border-mid-green shadow-denizen -translate-y-1'
                  : 'border border-dark-green/15 hover:-translate-y-0.5 hover:shadow-denizen-sm'
                }
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                focus:outline-none focus-visible:ring-4 focus-visible:ring-teal
              `}
            >
              {/* Image */}
              <div className="aspect-square overflow-hidden rounded-t-2xl">
                <img
                  src={image.thumbnail_url}
                  alt={image.title || `Location photo from ${sourceStyle.label}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Source badge */}
              <div
                className={`absolute top-2 left-2 ${sourceStyle.bg} border border-dark-green/15 px-2 py-0.5 rounded-full`}
              >
                <span className="text-[10px] font-display font-bold text-white">
                  {sourceStyle.label}
                </span>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-mid-green border border-dark-green/15 rounded-full flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-dark-green"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}

              {/* Attribution */}
              {image.attribution && (
                <div className="p-2 bg-white border-t border-dark-green/15">
                  <p className="text-[10px] font-body text-muted truncate">
                    {image.attribution}
                  </p>
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* "Create from scratch" option */}
      <div className="text-center pt-4">
        <button
          type="button"
          onClick={onCreateFromScratch}
          disabled={isDisabled}
          className={`btn-denizen-secondary w-full ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          Or create entirely from your conversation
        </button>
      </div>
    </div>
  )
}

export default GroundingImagePicker
